/**
 * §12 — UX writing, as a check rather than a memo.
 *
 * Interface copy is design, and these rules are as binding as the colour
 * tokens. A rule that lives only in a document is a rule that gets followed
 * until the afternoon someone is in a hurry, so the mechanical half of §12 is
 * enforced here and run against every copy table in the product.
 *
 * ## What this can and cannot catch
 *
 * It catches the failures that have a shape: an error code in a sentence a user
 * reads, the word "invalid", "You entered…", "No data available", a button
 * labelled "OK". It cannot catch copy that is merely bad — §12's actual test is
 * "read it aloud to a scheduler who has never seen the product; if they can act
 * on it without a follow-up question, it ships." No linter runs that test.
 *
 * So this is a floor, not a substitute for the reading. It exists because the
 * floor is where the regressions happen.
 */
const CHECKS = [
    {
        rule: 'Never expose internals',
        pattern: /\b(?:error|err|code|status)\s*[:#]?\s*\d{3}\b|\b[45]\d{2}\s+(?:error|response)\b|\b[A-Z][A-Z0-9]*_[A-Z0-9_]{2,}\b|\b(?:null|undefined|NaN)\b|\bstack trace\b/,
        fix: 'Say what happened in the reader\'s terms. "Denise\'s CPR certification expires before this shift. Renew it or assign someone else."',
    },
    {
        rule: 'Never blame the user',
        pattern: /\byou (?:entered|typed|provided|failed|forgot|must not|did not|didn't)\b/i,
        fix: 'Describe the constraint, not the person. "Visits can\'t be scheduled before the authorization starts on Aug 12."',
    },
    {
        rule: 'Say what happens next',
        pattern: /\b(?:invalid|malformed|unexpected error|an error occurred|something went wrong)\b|\bvalidation failed\b|\boperation failed\b/i,
        fix: 'Name the gap and the next step. "This visit can\'t be billed until the missing clock-out is resolved. Add an end time to release $91."',
    },
    {
        rule: 'Empty states offer the action',
        pattern: /\bno data(?: available)?\b|\bnothing to (?:display|show here)\b|\bn\/a\b|\bempty list\b|\bno results found\b/i,
        fix: 'Say what belongs here and how it gets here. "No exceptions today. Every visit verified cleanly."',
    },
    {
        rule: 'Buttons are verbs',
        pattern: /^\s*(?:ok|okay|submit|yes|no|done|go|apply|confirm)\s*$/i,
        fix: 'Name the action. "Clock in", "Verify visit", "Send offer", "Void claim".',
    },
    {
        rule: 'Reassure before informing',
        pattern: /\bnetwork error\b|\bconnection (?:lost|failed)\b|\bretry\?/i,
        fix: 'Lead with what is safe. "Offline — your work is saved. Everything syncs when you\'re back on signal."',
    },
    {
        rule: 'Sentence case everywhere except the eyebrow',
        // Three or more consecutive Capitalised words is Title Case, which §03
        // permits only in the 10.5px label. Proper nouns rarely run three deep.
        pattern: /(?:\b[A-Z][a-z]+\b[ ]){2}\b[A-Z][a-z]+\b/,
        fix: 'Sentence case. "Request an override", not "Request An Override".',
    },
];
/**
 * The build-plan vocabulary rule, which is not from §12.
 *
 * It came from a review of the first console build, where a placeholder page
 * told an agency owner that "Clients arrives with the care work in a later
 * slice." Nothing a user can see refers to how we build the product.
 */
const BUILD_PLAN = {
    // "release" was in this list and had to come out. §12's own example copy —
    // "Add an end time to release $91" — is about releasing held revenue, which
    // is this product's vocabulary, not ours. The check was wrong, and the clean
    // side of the test is what said so.
    rule: 'No references to the build plan',
    pattern: /\b(?:slice|sprint|phase|roadmap|backlog|milestone|epic|MVP|beta|coming soon|not yet implemented|under construction)\b/i,
    fix: "Describe the work the screen holds, in the reader's terms. An owner has a Monday, not a build plan.",
};
/**
 * Every rule this text breaks. Empty means it passes the mechanical checks —
 * which is the floor, not the bar.
 */
export function copyViolations(text) {
    return [BUILD_PLAN, ...CHECKS].flatMap((check) => {
        const found = check.pattern.exec(text);
        return found ? [{ rule: check.rule, match: found[0], fix: check.fix }] : [];
    });
}
/** The rule names, so a spec can assert every one of them is exercised. */
export const COPY_RULES = [BUILD_PLAN, ...CHECKS].map((check) => check.rule);
//# sourceMappingURL=ux-writing.js.map